import { useParams } from "react-router-dom";

import useEditStore from "@app/analytics/MeetingReport/stores/edits";
import useSearchStore from "@app/analytics/MeetingReport/stores/search";
import type { NoteItem } from "@app/analytics/MeetingReport/types";
import useAuth from "@app/common/auth/useAuth";
import type { NoteType } from "@app/common/types";

import EditableNoteSubSection from "./EditableNoteSubSection";
import NoteSubSectionWithAnnotations, {
  NoteSubSectionWithAnnotationsProps,
} from "./NoteSubSectionWithAnnotations";

type NoteSubSectionProps = Omit<
  NoteSubSectionWithAnnotationsProps,
  "searchResults"
> & {
  /* a custom list of items to display when editing. if undefined, will pull
  all items of the defined type straight from the edit store */
  editedItems?: NoteItem[];
};

const CREATABLE_TYPES: NoteType[] = ["action_item", "key_question", "note"];
const NOTETAKER_CREATABLE_TYPES: NoteType[] = [...CREATABLE_TYPES, "topic"];

const NoteSubSection = ({
  isNotetakerReport,
  type,
  ...props
}: NoteSubSectionProps) => {
  const { user } = useAuth();
  const { sessionId } = useParams() as { sessionId: string };

  const searchResults = useSearchStore(
    (state) => state.searchResultsByNoteTypeAndId[type]
  );

  const { isEditingNotes, isEditingSection } = useEditStore((state) => ({
    isEditingNotes: state.editingSection === "notes",
    isEditingSection:
      state.editingSection === "notes" &&
      ((type === "note" && state.editAccessLevel !== "none") ||
        state.editAccessLevel === "full"),
  }));

  if (isEditingSection) {
    const canCreateItems = (
      isNotetakerReport ? NOTETAKER_CREATABLE_TYPES : CREATABLE_TYPES
    ).includes(type);

    return (
      <EditableNoteSubSection
        canCreateNew={canCreateItems}
        items={props.editedItems}
        type={type}
        searchResults={searchResults}
        sessionId={sessionId}
        userId={user!.id}
      />
    );
  }

  return (
    <NoteSubSectionWithAnnotations
      {...props}
      searchResults={searchResults}
      // force all items to show if editing notes but this section is not editable
      maxItems={isEditingNotes ? props.items.length : props.maxItems}
      isNotetakerReport={isNotetakerReport}
      type={type}
    />
  );
};

export default NoteSubSection;
